import { authService } from '@/services/auth';
import { useEffect, useState, useRef, ReactNode } from 'react';
import { useAuthStore } from '@/store/use-auth-store';
import { FullScreenLoader } from '@/components/ui/loader';
import type { User } from '@/types/user.types';

// Auth Provider Types
export interface AuthProviderProps {
  children: ReactNode;
}

const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { setUser, setRoles, clearAuth, setAuthLoading, isAuthLoading, isAuthenticated } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const hasCheckedAuth = useRef(false);
  
  useEffect(() => {
    // Only check auth once on mount, or if user is not authenticated
    if (hasCheckedAuth.current || isAuthenticated) return;
    
    // Don't check auth if we're on login/register pages
    if (window.location.pathname.startsWith('/auth/')) {
      setLoading(false);
      setAuthLoading(false);
      hasCheckedAuth.current = true;
      return;
    }
    
    const checkAuth = async () => {
      try {
        setLoading(true);
        setAuthLoading(true);

        // Use profile endpoint to validate token and get user data
        // Tokens are automatically sent via HTTP-only cookies
        const response = await authService.getProfile();
        // getProfile returns User which may have roles as Role[] or string[]
        const userWithRoles = response as User & { roles?: Array<{ name: string }> | string[] };
        const { roles, ...user } = userWithRoles;

        if (user.status == "ENABLED") {
          setUser(user);
          // Extract role names - handle both Role[] and string[] formats
          const roleNames = roles 
            ? (Array.isArray(roles) && roles.length > 0 && typeof roles[0] === 'object' 
                ? roles.map((role: { name: string }) => role.name)
                : roles as string[])
            : null;
          setRoles(roleNames);
        } else {
          clearAuth();
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        clearAuth();
      } finally {
        setLoading(false);
        setAuthLoading(false);
        hasCheckedAuth.current = true;
      }
    };

    checkAuth();
  }, []);

  if (loading || isAuthLoading) {
    return <FullScreenLoader label="Loading..." />;
  }
  return <>{children}</>;
};

export default AuthProvider;